import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
export function PaidReportOutput() {
  // In production, this would come from API based on report type and user inputs
  const reportData = {
    reportName: 'Deep Self Decode',
    reportType: 'Full Diagnostic Report',
    // Your Internal Pattern Map
    internalPatternMap: {
      description: 'Your internal system operates through three coordinated forces that interact to maintain stability while managing perceived risk. These forces are not personality traits—they are adaptive mechanisms that formed to solve specific problems and now run automatically.',
      forces: [{
        name: 'Risk assessment mechanism',
        function: 'Continuously scans for situations requiring visibility, evaluation, or vulnerability. Operates before conscious awareness.'
      }, {
        name: 'Protection response system',
        function: 'Activates withdrawal or over-preparation when risk is detected. Prioritizes immediate safety over long-term range.'
      }, {
        name: 'Pattern reinforcement loop',
        function: 'Interprets relief as validation. Each cycle strengthens the association between exposure and threat, making the pattern more automatic.'
      }]
    },
    // Primary Internal Pattern
    primaryPattern: {
      name: 'Preemptive self-protection through strategic withdrawal',
      whatItIs: 'This pattern operates as an early-warning system that activates before situations requiring visibility or vulnerability fully develop. Rather than responding to actual threat, it responds to the possibility of threat.',
      whatItProtects: 'The pattern protects against experiences of exposure, judgment, or loss of control. It maintains a sense of safety by keeping you in positions where outcomes feel predictable and risk feels managed.',
      whatItTradesOff: 'In exchange for immediate relief and predictability, the pattern trades access to opportunities requiring visibility, limits your range of response, and prevents recalibration of what actually constitutes risk.'
    },
    // How This Pattern Operates
    patternMechanics: {
      trigger: 'Situations requiring visibility, evaluation, or vulnerability. The pattern activates on perceived exposure, not actual threat.',
      response: 'Internal risk calculation followed by preemptive withdrawal (physical or psychological) or excessive preparation to control all variables.',
      shortTermRelief: 'Immediate reduction in anxiety. Sense of safety through avoidance or over-control. Confirmation that the protective response "worked."',
      longTermCost: 'Opportunities requiring visibility are systematically avoided or approached with unsustainable effort. The pattern becomes more sensitive over time, activating in progressively lower-risk contexts. Range of response narrows.'
    },
    // Secondary Pattern Under Strain
    secondaryPattern: {
      description: 'When the primary pattern runs too long or encounters situations where withdrawal is not possible, a secondary pattern emerges: hyper-performance as a form of control.',
      mechanics: 'If you cannot withdraw, you over-deliver. If you cannot avoid visibility, you attempt to control every variable through excessive preparation. This is not ambition—it is the protection mechanism operating through a different channel.',
      cost: 'This secondary pattern is more exhausting than the primary one. It creates the appearance of high performance while operating from a constraint-based motivation. The internal experience is pressure, not flow.'
    },
    // Why This Pattern Exists
    patternOrigin: {
      formation: 'This pattern likely formed in an environment where visibility carried unpredictable consequences—whether through criticism, conditional acceptance, or inconsistent responses. The system learned that self-protection through withdrawal or control was more reliable than direct engagement.',
      persistence: 'The pattern persists because it continues to provide short-term relief. Each time you withdraw or over-prepare and avoid a negative outcome, the pattern is reinforced. The system interprets "nothing bad happened" as evidence that the protection was necessary.',
      adaptiveReliability: 'This pattern is not a flaw. It is a solution that worked in its original context. The issue is not that the pattern exists—it is that the pattern now operates automatically in contexts where it may no longer serve.'
    },
    // What This Pattern Costs Over Time
    patternCosts: {
      cognitive: 'Constant background scanning for potential exposure. Mental energy allocated to risk assessment rather than creative or strategic thinking. Decision-making slowed by excessive analysis or deferred entirely.',
      emotional: 'Chronic low-level anxiety that feels like "how things are." Difficulty accessing states of ease or flow. Relationships require more energy because vulnerability is continuously managed.',
      strategic: 'Opportunities requiring visibility are systematically filtered out before conscious consideration. Career trajectory shaped by what feels safe rather than what aligns with capability. Range of action narrows over time as the pattern becomes more sensitive.'
    },
    // Where This Pattern Constrains You Now
    currentConstraints: {
      decisions: 'Commitment feels like exposure. Decisions are slowed by excessive analysis, deferred to others, or avoided through inaction. The pattern interprets "staying open" as safety, but this prevents forward movement.',
      work: 'Opportunities requiring visibility—presentations, leadership, public work—are avoided or approached with unsustainable preparation. Delegation is difficult because control feels necessary. Work-in-progress is not shared until it feels "perfect," limiting feedback and iteration.',
      relationships: 'Intimacy requires vulnerability, which the pattern interprets as risk. Needs are difficult to express directly. Conflict triggers withdrawal. The constraint: connection requires exposure, which activates the protection mechanism.'
    },
    // Where This Pattern Loosens
    patternLoosening: {
      conditions: ['The pattern weakens in contexts that feel distinctly different from the original environment where it formed', 'The pattern shows gaps when there is a clear separation between trigger and response—moments where automatic activation does not occur', 'The pattern loosens when the accumulated cost becomes more visible than the perceived benefit', 'The pattern becomes less automatic in low-stakes situations where small variations in response occur naturally without conscious effort']
    },
    // Stability vs Evolution Tension
    stabilityTension: {
      description: 'Your internal system is designed to maintain stability, not to optimize for growth. This is not resistance or self-sabotage—it is how adaptive systems work.',
      mechanism: 'The pattern provides predictability. Even when it limits your range, it offers something valuable: a known way of operating. Evolution requires temporarily increasing uncertainty, which the system interprets as risk.',
      why: 'The system does not evaluate whether the pattern still serves. It evaluates whether the pattern still provides relief. As long as withdrawal or over-preparation reduces anxiety in the short term, the system will continue to select it.',
      implication: 'Change does not happen through insight alone. The system must encounter conditions where the old pattern stops providing relief or where a new response occurs naturally and proves reliable. This is why understanding the pattern is necessary but not sufficient.'
    },
    // Boundary Statement
    boundaries: 'This report explains how your internal system operates. It does not tell you what to do, how to change, or who to become. Implementation of any insights requires individual assessment and, where appropriate, professional support. This is a diagnostic document, not a prescription.'
  };
  return <>
      <Helmet data-id="element-2077">
        <title data-id="element-2078">{reportData.reportName} — Output | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-2079" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-2080">
        {/* Navigation */}
        <header data-id="element-2081">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-2082">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2083">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-2084">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-2085" />
                <span className="text-lg font-medium tracking-tight" data-id="element-2086">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-2087">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-2088">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" aria-label="Contact us on WhatsApp" data-id="element-2089">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        {/* Main Content */}
        <main className="pt-32 pb-24 px-6" data-id="element-2090">
          <div className="max-w-4xl mx-auto" data-id="element-2091">
            {/* Page Header */}
            <div className="mb-16" data-id="element-2092">
              <h1 className="text-4xl md:text-5xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2093">
                {reportData.reportName}
              </h1>
              <p className="text-sm text-[#F5F5F5]/50" data-id="element-2094">
                {reportData.reportType}
              </p>
            </div>

            {/* Section 1: Your Internal Pattern Map */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-2095">
              <div className="mb-8" data-id="element-2096">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-2097">
                  Your internal pattern map
                </h2>
              </div>

              <p className="text-[#F5F5F5]/70 leading-relaxed mb-8" data-id="element-2098">
                {reportData.internalPatternMap.description}
              </p>

              <div className="space-y-6" data-id="element-2099">
                {reportData.internalPatternMap.forces.map((force, index) => <div key={index} className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-2100">
                    <h3 className="text-base font-medium text-[#F5F5F5]/90 mb-2" data-id="element-2101">
                      {force.name}
                    </h3>
                    <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2102">
                      {force.function}
                    </p>
                  </div>)}
              </div>
            </section>

            {/* Section 2: Primary Internal Pattern */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-2103">
              <div className="mb-8" data-id="element-2104">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-2105">
                  Primary internal pattern
                </h2>
              </div>

              <div className="mb-8" data-id="element-2106">
                <h3 className="text-xl font-medium text-[#F5F5F5]/90 mb-4" data-id="element-2107">
                  {reportData.primaryPattern.name}
                </h3>
              </div>

              <div className="space-y-6" data-id="element-2108">
                <div data-id="element-2109">
                  <h4 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2110">
                    What it is
                  </h4>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2111">
                    {reportData.primaryPattern.whatItIs}
                  </p>
                </div>

                <div data-id="element-2112">
                  <h4 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2113">
                    What it protects
                  </h4>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2114">
                    {reportData.primaryPattern.whatItProtects}
                  </p>
                </div>

                <div data-id="element-2115">
                  <h4 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2116">
                    What it trades off
                  </h4>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2117">
                    {reportData.primaryPattern.whatItTradesOff}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 3: How This Pattern Operates */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-2118">
              <div className="mb-8" data-id="element-2119">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-2120">
                  How this pattern operates
                </h2>
              </div>

              <div className="space-y-6" data-id="element-2121">
                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-2122">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-2123">
                    Trigger
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2124">
                    {reportData.patternMechanics.trigger}
                  </p>
                </div>

                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-2125">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-2126">
                    Response
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2127">
                    {reportData.patternMechanics.response}
                  </p>
                </div>

                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-2128">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-2129">
                    Short-term relief
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2130">
                    {reportData.patternMechanics.shortTermRelief}
                  </p>
                </div>

                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-2131">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-2132">
                    Long-term cost
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2133">
                    {reportData.patternMechanics.longTermCost}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 4: Secondary Pattern Under Strain */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-2134">
              <div className="mb-8" data-id="element-2135">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-2136">
                  Secondary pattern under strain
                </h2>
              </div>

              <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-2137">
                {reportData.secondaryPattern.description}
              </p>

              <div className="space-y-6" data-id="element-2138">
                <div data-id="element-2139">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2140">
                    Mechanics
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2141">
                    {reportData.secondaryPattern.mechanics}
                  </p>
                </div>

                <div data-id="element-2142">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2143">
                    Cost
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2144">
                    {reportData.secondaryPattern.cost}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5: Why This Pattern Exists */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-2145">
              <div className="mb-8" data-id="element-2146">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-2147">
                  Why this pattern exists
                </h2>
              </div>

              <div className="space-y-6" data-id="element-2148">
                <div data-id="element-2149">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2150">
                    Formation
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2151">
                    {reportData.patternOrigin.formation}
                  </p>
                </div>

                <div data-id="element-2152">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2153">
                    Persistence
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2154">
                    {reportData.patternOrigin.persistence}
                  </p>
                </div>

                <div data-id="element-2155">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2156">
                    Adaptive reliability
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2157">
                    {reportData.patternOrigin.adaptiveReliability}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 6: What This Pattern Costs Over Time */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-2158">
              <div className="mb-8" data-id="element-2159">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-2160">
                  What this pattern costs over time
                </h2>
              </div>

              <div className="space-y-6" data-id="element-2161">
                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-2162">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-2163">
                    Cognitive
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2164">
                    {reportData.patternCosts.cognitive}
                  </p>
                </div>

                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-2165">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-2166">
                    Emotional
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2167">
                    {reportData.patternCosts.emotional}
                  </p>
                </div>

                <div className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-2168">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-2 uppercase tracking-wide" data-id="element-2169">
                    Strategic
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2170">
                    {reportData.patternCosts.strategic}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 7: Where This Pattern Constrains You Now */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-2171">
              <div className="mb-8" data-id="element-2172">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-2173">
                  Where this pattern constrains you now
                </h2>
              </div>

              <div className="space-y-6" data-id="element-2174">
                <div data-id="element-2175">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2176">
                    Decisions
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2177">
                    {reportData.currentConstraints.decisions}
                  </p>
                </div>

                <div data-id="element-2178">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2179">
                    Work
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2180">
                    {reportData.currentConstraints.work}
                  </p>
                </div>

                <div data-id="element-2181">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2182">
                    Relationships
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2183">
                    {reportData.currentConstraints.relationships}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 8: Where This Pattern Loosens */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-2184">
              <div className="mb-8" data-id="element-2185">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-2186">
                  Where this pattern loosens
                </h2>
              </div>

              <div className="space-y-4" data-id="element-2187">
                {reportData.patternLoosening.conditions.map((condition, index) => <div key={index} className="border-l-2 border-[#2B2B2B] pl-6" data-id="element-2188">
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2189">
                        {condition}
                      </p>
                    </div>)}
              </div>
            </section>

            {/* Section 9: Stability vs Evolution Tension */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]" data-id="element-2190">
              <div className="mb-8" data-id="element-2191">
                <h2 className="text-2xl font-medium tracking-tight" data-id="element-2192">
                  Stability vs evolution tension
                </h2>
              </div>

              <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-2193">
                {reportData.stabilityTension.description}
              </p>

              <div className="space-y-6" data-id="element-2194">
                <div data-id="element-2195">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2196">
                    Mechanism
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2197">
                    {reportData.stabilityTension.mechanism}
                  </p>
                </div>

                <div data-id="element-2198">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2199">
                    Why this matters
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2200">
                    {reportData.stabilityTension.why}
                  </p>
                </div>

                <div data-id="element-2201">
                  <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-3 uppercase tracking-wide" data-id="element-2202">
                    Implication
                  </h3>
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2203">
                    {reportData.stabilityTension.implication}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 10: Boundary Statement */}
            <section className="mb-16" data-id="element-2204">
              <div className="border border-[#2B2B2B] p-8" data-id="element-2205">
                <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2206">
                  {reportData.boundaries}
                </p>
              </div>
            </section>
          </div>
        </main>

        {/* Minimal Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" role="contentinfo" data-id="element-2207">
          <div className="max-w-7xl mx-auto text-center" data-id="element-2208">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-2209">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}